import type { Task, TaskAllocation, User } from '@/type';

import type { TaskFrameInfo } from '../type';

type CreateTaskAllocationsParams = {
  task: Task;
  frameInfo: TaskFrameInfo;
  unAssignedUserID: User['id'][];
};

export const createTaskAllocations = ({
  task,
  frameInfo,
  unAssignedUserID,
}: CreateTaskAllocationsParams): TaskAllocation[] => {
  const {
    startFrameSeq,
    endFrameSeq,
    fullFrameCount,
    userMoreOrEqualThanFrame,
    allocateRangeGap,
  } = frameInfo;

  if (!task.userTask) return [];

  const assignedUsers = task.userTask.filter(
    ut => !unAssignedUserID.includes(ut.userID),
  );
  const assignedUserCount = assignedUsers.length;

  if (userMoreOrEqualThanFrame) {
    return task.userTask.map(ut => {
      const isUnassigned = unAssignedUserID.includes(ut.userID);
      const frameIndex = assignedUsers.findIndex(au => au.userID === ut.userID);

      if (isUnassigned || frameIndex === -1 || frameIndex >= fullFrameCount) {
        return {
          userID: ut.userID,
          allocateRange: [0, 0] as [number, number],
          current: ut.doneFrameCount,
        };
      }

      const frameSeq = startFrameSeq + frameIndex;
      return {
        userID: ut.userID,
        allocateRange: [frameSeq, frameSeq] as [number, number],
        current: ut.doneFrameCount,
      };
    });
  }

  return task.userTask.map(ut => {
    const isUnassigned = unAssignedUserID.includes(ut.userID);

    if (isUnassigned) {
      return {
        userID: ut.userID,
        allocateRange: [0, 0] as [number, number],
        current: ut.doneFrameCount,
      };
    }

    const userIndex = assignedUsers.findIndex(au => au.userID === ut.userID);
    if (userIndex === -1) {
      return {
        userID: ut.userID,
        allocateRange: [0, 0] as [number, number],
        current: ut.doneFrameCount,
      };
    }

    const rangeStart = startFrameSeq + userIndex * allocateRangeGap;
    const isLastUser = userIndex === assignedUserCount - 1;
    const rangeEnd = isLastUser
      ? endFrameSeq
      : startFrameSeq + (userIndex + 1) * allocateRangeGap - 1;

    return {
      userID: ut.userID,
      allocateRange: [rangeStart, rangeEnd] as [number, number],
      current: ut.doneFrameCount,
    };
  });
};
